import { Button, Tag, Text } from '@wfp/react';
import React, { useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import TableMain from 'src/components/TableMain';
import { API_HOST, CURRENT_STATE_LABELS, STATUS_COLORS } from 'src/constants';
import { receiveAPIResponseHoldingData } from 'src/redux/data/actions';
import { apiCallAsync } from 'src/redux/sagas';
import { REPORT_LIST_DATA_KEY } from 'src/redux/view/constants';
import { calcMergeStatus, openACRFile, showNotification } from 'src/utils';
import OperationIdCell from '../OperationIdCell';
import ACRFileCell from './ACRFileCell';

const PublicationStatusTab = ({ data, filterText, filterSelect, period }) => {
  const dispatch = useDispatch();
  const [reportSubmitting, setReportSubmitting] = useState(null);

  const processedData = useMemo(() => {
    if (!data) return [];

    return data.map(reportRow => ({
      ...reportRow,
      merge_status: calcMergeStatus(reportRow),
    }));
  }, [data]);

  async function submitReport(pk) {
    setReportSubmitting(pk);
    const url = `${API_HOST}/api/acr-merge/submit/${pk}/`;

    try {
      const response = await apiCallAsync(url, null, 'post');

      dispatch(
        receiveAPIResponseHoldingData(response.data, REPORT_LIST_DATA_KEY),
      );
      showNotification('Report submitted successfully', 'success');
      setReportSubmitting(null);
    } catch (err) {
      console.error(err);

      showNotification('Error while submitting the report', 'error');
      setReportSubmitting(null);
    }
  }

  function calcReportSubmitted({ link, preview_link, pk }) {
    if (link) return <Text>Yes</Text>;
    if (preview_link) {
      return (
        <Button
          disabled={reportSubmitting === pk}
          onClick={() => submitReport(pk)}
          data-test-id="submit-button"
        >
          Submit
        </Button>
      );
    }

    return <Text>No</Text>;
  }

  const columns = [
    {
      Header: 'Operation ID',
      accessor: 'project_code',
      id: 'project_code_column',
      Filter: column => filterText(column),
      Cell: ({ row }) => <OperationIdCell row={row} />,
    },
    {
      Header: 'ACR 5',
      accessor: 'acr_5_file_url',
      id: 'acr_5_file_url_column',
      Filter: () => null,
      Cell: ({ value, row: { original } }) => (
        <ACRFileCell
          type="acr_5"
          value={value}
          rowValues={original}
          period={period}
        />
      ),
    },
    {
      Header: 'ACR 1',
      accessor: 'acr_1_file_url',
      id: 'acr_1_file_url_column',
      Filter: () => null,
      Cell: ({ value, row: { original } }) => (
        <ACRFileCell
          type="acr_1"
          value={value}
          rowValues={original}
          period={period}
        />
      ),
    },
    {
      Header: 'Project Status',
      accessor: 'current_status',
      id: 'current_status_column',
      className: 'current-status-column',
      Cell: ({ value }) => {
        const statusLabel = CURRENT_STATE_LABELS[value] || value;
        const colors = STATUS_COLORS[statusLabel] || {
          background: '#E0E0E0',
          color: '#333333',
        };
        return (
          <Tag
            style={{
              backgroundColor: colors.background,
              color: colors.color,
            }}
          >
            {statusLabel}
          </Tag>
        );
      },
      Filter: column => filterSelect('current_status', column),
    },
    {
      Header: 'Merge Status',
      accessor: 'merge_status',
      id: 'merge_status_column',
      filter: 'equals',
      Filter: column => filterSelect('merge_status', column, true),
      Cell: ({ value }) => (
        <div className="publication-merge-status">{value || '-'}</div>
      ),
    },
    {
      Header: 'Report Submitted',
      accessor: ({ link, preview_link }) => {
        if (link) return 'Yes';
        if (preview_link) return 'Submit';
        return 'No';
      },
      id: 'report_submitted_column',
      Filter: () => null,
      Cell: ({ row: { original } }) => calcReportSubmitted(original),
    },
    {
      Header: 'Report Link',
      accessor: ({ link, preview_link }) => {
        if (link) return 'Show submitted ACR';
        if (preview_link) return 'Show ACR preview';
        return '';
      },
      id: 'report_link_column',
      Filter: () => null,
      Cell: ({
        row: {
          original: { link, preview_link },
        },
      }) => {
        if (link)
          return (
            <a
              className="acr-file-link file-submitted"
              href={link}
              target="_blank"
              rel="noreferrer"
              data-test-id="show-submitted-acr-link"
            >
              Show submitted ACR
            </a>
          );
        if (preview_link)
          return (
            <div
              className="acr-file-link"
              onClick={() => openACRFile(`https://${preview_link}`)}
              data-test-id="show-acr-preview-link"
            >
              Show ACR preview
            </div>
          );
        return '';
      },
    },
  ];

  return (
    <TableMain
      data={processedData}
      loading={!processedData || processedData.length === 0}
      columns={columns}
      pagination="client"
      className="-striped -highlight"
      isFilterable
      initialState={{
        sortBy: [
          {
            id: 'project_code_column',
            desc: false,
          },
        ],
      }}
    />
  );
};

export default PublicationStatusTab;
